import React from "react";
import { GoogleLogo, FacebookLogo, CaretLeft, CaretRight, Circle } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

const STATUS_COLOR = {
  live: "text-success",
  paused: "text-warning",
  closed: "text-muted-foreground",
};

export default function Sidebar({ collapsed, onToggleCollapse, accounts, activePlatform, onSelectPlatform, mobileOpen, onCloseMobile }) {
  const googleAccounts = accounts.filter((a) => a.platform === "google" && a.status !== "closed");
  const metaAccounts = accounts.filter((a) => a.platform === "meta" && a.status !== "closed");

  const content = (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-4 py-3">
        {!collapsed && <span className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Accounts</span>}
        <button onClick={onToggleCollapse} className="hidden lg:flex h-6 w-6 items-center justify-center rounded hover:bg-accent transition-colors" data-testid="sidebar-collapse-button">
          {collapsed ? <CaretRight className="h-3.5 w-3.5" /> : <CaretLeft className="h-3.5 w-3.5" />}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-2 space-y-4 pb-4">
        <button
          onClick={() => onSelectPlatform(null)}
          data-testid="sidebar-filter-all"
          className={cn(
            "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-colors",
            !activePlatform ? "bg-primary/10 text-primary font-medium" : "hover:bg-accent text-muted-foreground"
          )}
        >
          <Circle className="h-3.5 w-3.5 shrink-0" weight="fill" />
          {!collapsed && <span>All Campaigns</span>}
        </button>

        <div>
          <div className="flex items-center gap-2 px-2.5 mb-1.5">
            <GoogleLogo className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            {!collapsed && <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Google Ads</span>}
          </div>
          <button
            onClick={() => onSelectPlatform("google")}
            data-testid="sidebar-filter-google"
            className={cn(
              "w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm mb-1 transition-colors",
              activePlatform === "google" ? "bg-primary/10 text-primary font-medium" : "hover:bg-accent"
            )}
          >
            {!collapsed && <span>All Google accounts</span>}
          </button>
          {googleAccounts.length === 0 && !collapsed && (
            <p className="text-xs text-muted-foreground/60 px-2.5 py-1 italic">No accounts connected</p>
          )}
          {googleAccounts.map((acc) => (
            <div key={acc.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs" data-testid={`sidebar-account-${acc.id}`}>
              <Circle className={cn("h-2 w-2 shrink-0", STATUS_COLOR[acc.status])} weight="fill" />
              {!collapsed && <span className="truncate">{acc.account_name}</span>}
            </div>
          ))}
        </div>

        <div>
          <div className="flex items-center gap-2 px-2.5 mb-1.5">
            <FacebookLogo className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            {!collapsed && <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Meta Ads</span>}
          </div>
          <button
            onClick={() => onSelectPlatform("meta")}
            data-testid="sidebar-filter-meta"
            className={cn(
              "w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm mb-1 transition-colors",
              activePlatform === "meta" ? "bg-primary/10 text-primary font-medium" : "hover:bg-accent"
            )}
          >
            {!collapsed && <span>All Meta accounts</span>}
          </button>
          {metaAccounts.length === 0 && !collapsed && (
            <p className="text-xs text-muted-foreground/60 px-2.5 py-1 italic">No accounts connected</p>
          )}
          {metaAccounts.map((acc) => (
            <div key={acc.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs" data-testid={`sidebar-account-${acc.id}`}>
              <Circle className={cn("h-2 w-2 shrink-0", STATUS_COLOR[acc.status])} weight="fill" />
              {!collapsed && <span className="truncate">{acc.account_name}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <aside
        className={cn(
          "hidden lg:block fixed top-16 bottom-0 left-0 border-r border-border bg-background transition-all duration-200 z-30",
          collapsed ? "w-16" : "w-64"
        )}
      >
        {content}
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/60" onClick={onCloseMobile} />
          <aside className="relative w-64 bg-background border-r border-border h-full">{content}</aside>
        </div>
      )}
    </>
  );
}
